import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { resetClinic } from "../lib/clinicState";
import { leaveSupportMode } from "../components/SupportModeBanner";

// MT-12B: platform console. Everything here is authorised SERVER-SIDE from the
// SAAS_SUPERADMIN_EMAILS allowlist; the page only renders what /auth/me says.
export default function SuperAdmin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reason, setReason] = useState({});
  const [err, setErr] = useState("");
  const load = () => api.get("/saas/tenants").then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || "Not authorised"));
  useEffect(() => { load(); }, []);

  const enter = async (t) => {
    const why = (reason[t.tenant_id] || "").trim();
    if (why.length < 8) { toast.error("Enter a reason / ticket reference (min 8 characters)"); return; }
    try {
      if (user?.support_mode) await api.post("/saas/support/exit").catch(() => {});
      const r = await api.post("/saas/support/enter", { tenant_id: t.tenant_id, reason: why });
      resetClinic();                                   // nothing from the previous clinic survives
      try { sessionStorage.clear(); } catch { /* noop */ }
      toast.success(`Support mode: ${r.data.clinic_name || r.data.tenant_name} until ${new Date(r.data.expires_at).toLocaleTimeString()}`);
      window.location.href = "/dashboard";
    } catch (e) { toast.error(e.response?.data?.detail || "Could not enter support mode"); }
  };
  const setStatus = async (t, status) => {
    try { await api.post(`/saas/tenants/${t.tenant_id}/status`, { status, reason: reason[t.tenant_id] || "" }); toast.success(`${t.name}: ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setPlan = async (t, plan_id) => {
    try { await api.post(`/saas/tenants/${t.tenant_id}/entitlement`, { plan_id }); toast.success(`${t.name}: plan ${plan_id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!user?.is_superadmin) return <Layout><div className="p-8 text-slate-600" data-testid="superadmin-denied">Platform console is restricted to allow-listed superadmins.</div></Layout>;
  return (
    <Layout>
      <div className="mx-auto max-w-[1200px] p-6 space-y-6" data-testid="superadmin-console">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-semibold">Platform console</h1>
          {user?.support_mode && <button onClick={leaveSupportMode} className="px-3 py-1.5 rounded bg-slate-900 text-amber-300" data-testid="superadmin-exit-support">Exit support mode</button>}
        </div>
        {err && <div className="text-rose-700" data-testid="superadmin-error">{err}</div>}
        <div className="grid gap-4">
          {(data?.tenants || []).map((t) => (
            <div key={t.tenant_id} className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid={`tenant-card-${t.tenant_id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="font-semibold" data-testid={`tenant-clinic-name-${t.tenant_id}`}>{t.clinic_name || t.name}</div>
                {t.clinic_name && t.clinic_name !== t.name && <span className="text-xs text-slate-500">platform name: {t.name}</span>}
                <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`} data-testid={`tenant-status-${t.tenant_id}`}>{t.status}</span>
                {t.is_legacy && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">legacy</span>}
                <span className="text-xs text-slate-500">{t.tenant_id} · plan {t.entitlement?.plan_id || t.plan_id} · {t.members} active members · {t.open_invites} open invites</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input value={reason[t.tenant_id] || ""} onChange={(e) => setReason({ ...reason, [t.tenant_id]: e.target.value })} placeholder="Reason / support ticket (required)" className="flex-1 min-w-[260px] border border-slate-300 rounded px-2 py-1 text-sm" data-testid={`support-reason-${t.tenant_id}`} />
                <button onClick={() => enter(t)} disabled={t.status !== "ACTIVE"} className="px-3 py-1.5 rounded bg-amber-500 text-slate-950 text-sm font-semibold disabled:opacity-40" data-testid={`support-enter-${t.tenant_id}`}>Enter support mode</button>
                {!t.is_legacy && (t.status === "ACTIVE"
                  ? <button onClick={() => setStatus(t, "SUSPENDED")} className="px-3 py-1.5 rounded border border-rose-300 text-rose-700 text-sm" data-testid={`tenant-suspend-${t.tenant_id}`}>Suspend</button>
                  : <button onClick={() => setStatus(t, "ACTIVE")} className="px-3 py-1.5 rounded border border-emerald-300 text-emerald-700 text-sm" data-testid={`tenant-activate-${t.tenant_id}`}>Activate</button>)}
                <select value={t.entitlement?.plan_id || t.plan_id || ""} onChange={(e) => setPlan(t, e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm" data-testid={`tenant-plan-${t.tenant_id}`}>
                  {(data?.plans || []).map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
